import { RIBBON_CONFIG, type RibbonTuning } from './config'
import { RibbonGuide } from './guide'

const EPSILON = 1e-7

const normalize = (
  x: number,
  y: number,
  z: number,
): readonly [number, number, number] => {
  const inverseLength = 1 / Math.max(EPSILON, Math.hypot(x, y, z))
  return [x * inverseLength, y * inverseLength, z * inverseLength]
}

export interface ChainDiagnostics {
  frameCorrections: number
  maxConstraintError: number
  minAdjacentFrameDot: number
}

export class ChainSimulator {
  readonly frameTextureData: Float32Array
  readonly positionTextureData: Float32Array

  private readonly frames: Float32Array
  private readonly guide: RibbonGuide
  private readonly guidePositions: Float32Array
  private readonly positions: Float32Array
  private readonly previousPositions: Float32Array
  private readonly restLength: number

  private diagnostics: ChainDiagnostics = {
    frameCorrections: 0,
    maxConstraintError: 0,
    minAdjacentFrameDot: 1,
  }

  constructor(
    private readonly jointCount: number,
    private readonly ribbonLength: number,
    private readonly tuning: RibbonTuning,
  ) {
    this.positions = new Float32Array(jointCount * 3)
    this.previousPositions = new Float32Array(jointCount * 3)
    this.guidePositions = new Float32Array(jointCount * 3)
    this.frames = new Float32Array(jointCount * 3)
    this.positionTextureData = new Float32Array(jointCount * 4)
    this.frameTextureData = new Float32Array(jointCount * 4)
    this.restLength = ribbonLength / (jointCount - 1)
    this.guide = new RibbonGuide(jointCount, ribbonLength)
    this.reset(0)
  }

  getDiagnostics(): ChainDiagnostics {
    return { ...this.diagnostics }
  }

  getFrames(): Float32Array {
    return this.frames
  }

  getPositions(): Float32Array {
    return this.positions
  }

  reset(progress: number): void {
    this.guide.write(progress, this.guidePositions)
    this.positions.set(this.guidePositions)
    this.previousPositions.set(this.guidePositions)
    this.solveConstraints(this.tuning.constraintIterations)
    this.updateFramesAndTextures()
  }

  step(deltaSeconds: number, progress: number, reducedMotion: boolean): void {
    if (reducedMotion) {
      this.reset(progress)
      return
    }

    this.guide.write(progress, this.guidePositions)
    const cappedDelta = Math.min(
      RIBBON_CONFIG.motion.maxDelta,
      Math.max(0, deltaSeconds),
    )
    const substepDelta = cappedDelta / RIBBON_CONFIG.motion.substeps

    for (
      let substep = 0;
      substep < RIBBON_CONFIG.motion.substeps;
      substep += 1
    ) {
      this.integrate(substepDelta)
      this.solveConstraints(this.tuning.constraintIterations)
    }

    this.updateFramesAndTextures()
  }

  private integrate(deltaSeconds: number): void {
    const velocityRetention = Math.pow(
      this.tuning.simulationDamping,
      deltaSeconds * 60,
    )
    const accelerationScale =
      this.tuning.guideStiffness * deltaSeconds * deltaSeconds
    const gravity =
      RIBBON_CONFIG.motion.gravity * deltaSeconds * deltaSeconds

    for (let joint = 1; joint < this.jointCount; joint += 1) {
      const offset = joint * 3
      const currentX = this.positions[offset]
      const currentY = this.positions[offset + 1]
      const currentZ = this.positions[offset + 2]
      const velocityX =
        (currentX - this.previousPositions[offset]) * velocityRetention
      const velocityY =
        (currentY - this.previousPositions[offset + 1]) * velocityRetention
      const velocityZ =
        (currentZ - this.previousPositions[offset + 2]) * velocityRetention

      this.previousPositions[offset] = currentX
      this.previousPositions[offset + 1] = currentY
      this.previousPositions[offset + 2] = currentZ

      this.positions[offset] =
        currentX +
        velocityX +
        (this.guidePositions[offset] - currentX) * accelerationScale
      this.positions[offset + 1] =
        currentY +
        velocityY +
        (this.guidePositions[offset + 1] - currentY) * accelerationScale +
        gravity
      this.positions[offset + 2] =
        currentZ +
        velocityZ +
        (this.guidePositions[offset + 2] - currentZ) * accelerationScale
    }

    this.pinPulledEnd()
  }

  private pinPulledEnd(): void {
    this.positions[0] = this.guidePositions[0]
    this.positions[1] = this.guidePositions[1]
    this.positions[2] = this.guidePositions[2]
    this.previousPositions[0] = this.guidePositions[0]
    this.previousPositions[1] = this.guidePositions[1]
    this.previousPositions[2] = this.guidePositions[2]
  }

  private solveConstraints(iterations: number): void {
    const safeIterations = Math.max(1, Math.round(iterations))

    for (let iteration = 0; iteration < safeIterations; iteration += 1) {
      this.pinPulledEnd()

      for (let joint = 0; joint < this.jointCount - 1; joint += 1) {
        const firstOffset = joint * 3
        const secondOffset = firstOffset + 3
        const deltaX =
          this.positions[secondOffset] - this.positions[firstOffset]
        const deltaY =
          this.positions[secondOffset + 1] - this.positions[firstOffset + 1]
        const deltaZ =
          this.positions[secondOffset + 2] - this.positions[firstOffset + 2]
        const distance = Math.max(EPSILON, Math.hypot(deltaX, deltaY, deltaZ))
        const correction = (distance - this.restLength) / distance

        if (joint === 0) {
          this.positions[secondOffset] -= deltaX * correction
          this.positions[secondOffset + 1] -= deltaY * correction
          this.positions[secondOffset + 2] -= deltaZ * correction
        } else {
          const halfCorrection = correction * 0.5
          this.positions[firstOffset] += deltaX * halfCorrection
          this.positions[firstOffset + 1] += deltaY * halfCorrection
          this.positions[firstOffset + 2] += deltaZ * halfCorrection
          this.positions[secondOffset] -= deltaX * halfCorrection
          this.positions[secondOffset + 1] -= deltaY * halfCorrection
          this.positions[secondOffset + 2] -= deltaZ * halfCorrection
        }
      }
    }

    this.pinPulledEnd()
  }

  private readTangent(joint: number): readonly [number, number, number] {
    const previousJoint = Math.max(0, joint - 1)
    const nextJoint = Math.min(this.jointCount - 1, joint + 1)
    const previousOffset = previousJoint * 3
    const nextOffset = nextJoint * 3

    return normalize(
      this.positions[nextOffset] - this.positions[previousOffset],
      this.positions[nextOffset + 1] - this.positions[previousOffset + 1],
      this.positions[nextOffset + 2] - this.positions[previousOffset + 2],
    )
  }

  private updateFramesAndTextures(): void {
    let corrections = 0
    let minAdjacentDot = 1
    let [previousTangentX, previousTangentY, previousTangentZ] =
      this.readTangent(0)
    let [previousWidthX, previousWidthY, previousWidthZ] = normalize(
      -previousTangentY,
      previousTangentX,
      0,
    )

    if (Math.hypot(previousWidthX, previousWidthY, previousWidthZ) < 0.1) {
      ;[previousWidthX, previousWidthY, previousWidthZ] = normalize(
        0,
        -previousTangentZ,
        previousTangentY,
      )
    }

    this.frames[0] = previousWidthX
    this.frames[1] = previousWidthY
    this.frames[2] = previousWidthZ

    for (let joint = 1; joint < this.jointCount; joint += 1) {
      const [tangentX, tangentY, tangentZ] = this.readTangent(joint)
      let widthX = previousWidthX
      let widthY = previousWidthY
      let widthZ = previousWidthZ
      const axisX =
        previousTangentY * tangentZ - previousTangentZ * tangentY
      const axisY =
        previousTangentZ * tangentX - previousTangentX * tangentZ
      const axisZ =
        previousTangentX * tangentY - previousTangentY * tangentX
      const sine = Math.hypot(axisX, axisY, axisZ)
      const cosine = Math.min(
        1,
        Math.max(
          -1,
          previousTangentX * tangentX +
            previousTangentY * tangentY +
            previousTangentZ * tangentZ,
        ),
      )

      if (sine > EPSILON) {
        const inverseSine = 1 / sine
        const unitAxisX = axisX * inverseSine
        const unitAxisY = axisY * inverseSine
        const unitAxisZ = axisZ * inverseSine
        const crossX = unitAxisY * widthZ - unitAxisZ * widthY
        const crossY = unitAxisZ * widthX - unitAxisX * widthZ
        const crossZ = unitAxisX * widthY - unitAxisY * widthX
        const axisDotWidth =
          unitAxisX * widthX + unitAxisY * widthY + unitAxisZ * widthZ
        const oneMinusCosine = 1 - cosine

        widthX =
          widthX * cosine +
          crossX * sine +
          unitAxisX * axisDotWidth * oneMinusCosine
        widthY =
          widthY * cosine +
          crossY * sine +
          unitAxisY * axisDotWidth * oneMinusCosine
        widthZ =
          widthZ * cosine +
          crossZ * sine +
          unitAxisZ * axisDotWidth * oneMinusCosine
      }

      const widthDotTangent =
        widthX * tangentX + widthY * tangentY + widthZ * tangentZ
      ;[widthX, widthY, widthZ] = normalize(
        widthX - tangentX * widthDotTangent,
        widthY - tangentY * widthDotTangent,
        widthZ - tangentZ * widthDotTangent,
      )

      let adjacentDot =
        widthX * previousWidthX +
        widthY * previousWidthY +
        widthZ * previousWidthZ

      if (adjacentDot < 0) {
        widthX *= -1
        widthY *= -1
        widthZ *= -1
        adjacentDot *= -1
        corrections += 1
      }

      minAdjacentDot = Math.min(minAdjacentDot, adjacentDot)
      const frameOffset = joint * 3
      this.frames[frameOffset] = widthX
      this.frames[frameOffset + 1] = widthY
      this.frames[frameOffset + 2] = widthZ
      previousTangentX = tangentX
      previousTangentY = tangentY
      previousTangentZ = tangentZ
      previousWidthX = widthX
      previousWidthY = widthY
      previousWidthZ = widthZ
    }

    let maxConstraintError = 0

    for (let joint = 0; joint < this.jointCount; joint += 1) {
      const sourceOffset = joint * 3
      const textureOffset = joint * 4
      this.positionTextureData[textureOffset] = this.positions[sourceOffset]
      this.positionTextureData[textureOffset + 1] =
        this.positions[sourceOffset + 1]
      this.positionTextureData[textureOffset + 2] =
        this.positions[sourceOffset + 2]
      this.positionTextureData[textureOffset + 3] = 1
      this.frameTextureData[textureOffset] = this.frames[sourceOffset]
      this.frameTextureData[textureOffset + 1] =
        this.frames[sourceOffset + 1]
      this.frameTextureData[textureOffset + 2] =
        this.frames[sourceOffset + 2]
      this.frameTextureData[textureOffset + 3] = 1

      if (joint < this.jointCount - 1) {
        const nextOffset = sourceOffset + 3
        const distance = Math.hypot(
          this.positions[nextOffset] - this.positions[sourceOffset],
          this.positions[nextOffset + 1] - this.positions[sourceOffset + 1],
          this.positions[nextOffset + 2] - this.positions[sourceOffset + 2],
        )
        maxConstraintError = Math.max(
          maxConstraintError,
          Math.abs(distance - this.restLength),
        )
      }
    }

    this.diagnostics = {
      frameCorrections: corrections,
      maxConstraintError,
      minAdjacentFrameDot: minAdjacentDot,
    }
  }
}
